import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { listWatchlistItems } from '../api/api';

const WatchlistContext = createContext(null);
const WATCHLIST_PAGE_SIZE = 500;
const WATCHLIST_PAGE_GUARD = 30;

function getItemsFromPaginatedPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function getNextPaginatedParams(nextUrl) {
  if (!nextUrl) return null;
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(nextUrl, base);
    return Object.fromEntries(url.searchParams.entries());
  } catch {
    return null;
  }
}

async function fetchAllWatchlistItems(params = {}) {
  let payload = await listWatchlistItems({ page_size: WATCHLIST_PAGE_SIZE, ...params });
  let items = getItemsFromPaginatedPayload(payload);
  let nextParams = getNextPaginatedParams(payload?.next);
  let guard = 0;

  while (nextParams && guard < WATCHLIST_PAGE_GUARD) {
    guard += 1;
    payload = await listWatchlistItems(nextParams);
    items = items.concat(getItemsFromPaginatedPayload(payload));
    nextParams = getNextPaginatedParams(payload?.next);
  }

  return items;
}

export function WatchlistProvider({ children }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const itemsRef = useRef(items);
  const requestRef = useRef(null);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const replaceItems = useCallback((nextItems) => {
    const normalized = Array.isArray(nextItems) ? nextItems : [];
    itemsRef.current = normalized;
    setItems(normalized);
    setLoaded(true);
  }, []);

  const refresh = useCallback(async ({ force = false } = {}) => {
    if (!force && loaded) return itemsRef.current;
    if (!force && requestRef.current) return requestRef.current;

    setLoading(true);
    setError(null);
    const request = fetchAllWatchlistItems()
      .then((nextItems) => {
        replaceItems(nextItems);
        return nextItems;
      })
      .catch((err) => {
        setError(err);
        if (!loaded) {
          itemsRef.current = [];
          setItems([]);
        }
        throw err;
      })
      .finally(() => {
        requestRef.current = null;
        setLoading(false);
      });

    requestRef.current = request;
    return request;
  }, [loaded, replaceItems]);

  useEffect(() => {
    const reset = () => {
      requestRef.current = null;
      itemsRef.current = [];
      setItems([]);
      setLoaded(false);
      setError(null);
      setLoading(false);
    };
    window.addEventListener('valpha:logout', reset);
    return () => window.removeEventListener('valpha:logout', reset);
  }, []);

  const value = useMemo(() => ({
    items,
    loaded,
    loading,
    error,
    refresh,
    setItems: replaceItems,
  }), [items, loaded, loading, error, refresh, replaceItems]);

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  );
}

export function useWatchlistData() {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error('useWatchlistData must be used within a WatchlistProvider');
  }
  return context;
}
